import { QueryClient, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for checking if queries are fetching.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface IsFetchingTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, checks all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to check */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
}

/**
 * Returns the number of queries that are currently fetching for matching Tellescope queries.
 * Useful for showing global loading indicators or tracking ongoing requests.
 *
 * @param args - The isFetching queries arguments
 * @returns The number of queries currently fetching
 *
 * @example
 * ```tsx
 * // Check if any queries for a method are fetching
 * const fetchingCount = isFetchingTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Check specific query
 * const isFetching = isFetchingTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * }) > 0;
 *
 * // Check with filters
 * const activeFetchingCount = isFetchingTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     stale: true, // Only count stale queries
 *   },
 * });
 * ```
 */
export const isFetchingTellescopeQueries = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
}: IsFetchingTellescopeQueriesArgs<Model, Method, TError>): number => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.isFetching({
    queryKey,
    ...filters,
  } as QueryFilters<TellescopeQueryKey<Model, Method>>);
};
